import React, { useMemo } from 'react';
import styled from 'styled-components';
import { TokenSet } from '../../styles/tokens';
import { useLanguage } from '../../contexts/LanguageContext';
import { DeckType } from '../../../domain/entities/Room';
import { TSHIRT_ORDER, isTShirtSize } from '../../../domain/valueObjects/TShirtDeck';
import { getCardLabel } from '../../i18n/cardLabels';

const Box = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.bgSurface};
  border: 1px solid ${p => p.tokens.border};
  border-radius: 16px;
  padding: 12px 16px;
`;

const Title = styled.div<{ tokens: TokenSet }>`
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${p => p.tokens.textMuted};
  margin-bottom: 8px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Row = styled.div<{ tokens: TokenSet }>`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${p => p.tokens.bgElevated};
  border-radius: 10px;
  padding: 8px 12px;
`;

const VoteBubble = styled.div<{ tokens: TokenSet }>`
  background: ${p => p.tokens.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 900;
  border-radius: 8px;
  min-width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 6px;
`;

const SpeakerText = styled.div<{ tokens: TokenSet }>`
  font-size: 13px;
  color: ${p => p.tokens.text};
  font-weight: 500;
  span {
    font-weight: 800;
    color: ${p => p.tokens.primary};
  }
`;

const ConsensusBox = styled.div<{ tokens: TokenSet }>`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${p => p.tokens.success};
  font-weight: 700;
  font-size: 15px;
`;

interface TalkSuggestionsProps {
  votes: Record<string, string>;
  participantNames: Record<string, string>;
  tokens: TokenSet;
  deckType?: DeckType;
}

function compareVotes(a: string, b: string, deckType: DeckType): number {
  if (deckType === 'tshirt') {
    const aIdx = isTShirtSize(a) ? TSHIRT_ORDER.indexOf(a) : 100;
    const bIdx = isTShirtSize(b) ? TSHIRT_ORDER.indexOf(b) : 100;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return a.localeCompare(b);
  }
  const aNum = parseFloat(a);
  const bNum = parseFloat(b);
  return isNaN(aNum) || isNaN(bNum) ? a.localeCompare(b) : aNum - bNum;
}

export const TalkSuggestions: React.FC<TalkSuggestionsProps> = ({ votes, participantNames, tokens, deckType = 'fibonacci' }) => {
  const { t } = useLanguage();
  const suggestions = useMemo(() => {
    const groups: Record<string, string[]> = {};
    Object.entries(votes).forEach(([uid, v]) => {
      if (!groups[v]) groups[v] = [];
      groups[v].push(uid);
    });

    const uniqueValues = Object.keys(groups);
    if (uniqueValues.length <= 1) return [];

    return uniqueValues
      .map(vote => {
        const uids = groups[vote];
        const idx = Math.floor(Math.random() * uids.length);
        return { vote, speakerName: participantNames[uids[idx]] || uids[idx] };
      })
      .sort((a, b) => compareVotes(a.vote, b.vote, deckType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(votes), JSON.stringify(participantNames), deckType]);

  const isConsensus = suggestions.length === 0;

  return (
    <Box tokens={tokens}>
      <Title tokens={tokens}>{t.discussionTitle}</Title>
      {isConsensus ? (
        <ConsensusBox tokens={tokens}>
          {t.consensusMsg}
        </ConsensusBox>
      ) : (
        <List>
          {suggestions.map(s => {
            const label = getCardLabel(s.vote, deckType, t);
            return (
              <Row key={s.vote} tokens={tokens}>
                <VoteBubble tokens={tokens}>{label}</VoteBubble>
                <SpeakerText tokens={tokens}>
                  <span>{s.speakerName}</span>, {t.whyVoted} <span>{label}</span>?
                </SpeakerText>
              </Row>
            );
          })}
        </List>
      )}
    </Box>
  );
};
